import {
    AnalyticsModelErrorTypeCodes,
    IAnalyticsModelResponse,
    ICorporationResponse,
    ILocationResponse,
} from '@kcftech/prometheus-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TableColumn } from 'components/data_table/types';
import { DateRelativeToNow } from 'components/date_time/DateRelativeToNow';
import { BooleanStatusIcon } from 'components/icon/BooleanStatusIcon';
import { ArrowLinkCell } from 'components/link/ArrowLink';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { TablePageLayout } from 'components/page/styled';
import { CRUDTable } from 'components/table/CRUDTable';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    AnalyticsModelsFilter,
    useAnalyticsModelPagedDataSource,
} from 'custom_hooks/data_hooks/data_source/analyticsModelDataSource';
import { getTabRouteFromNavType } from 'pages/Models/AnalyticsModelTab/AnalyticsModelsTabs';
import { CreateAnalyticsModelModal } from 'pages/Models/AnalyticsModelTab/CreateAnalyticsModelModal';
import { EditAnalyticsModelModal } from 'pages/Models/AnalyticsModelTab/EditAnalyticsModelModal';
import { useEffect, useMemo, useState } from 'react';
import { Lookup } from 'utils/types';

const ModelErrors: Lookup<AnalyticsModelErrorTypeCodes, string> = {
    [AnalyticsModelErrorTypeCodes.FailureRateReached]: 'Failure Rate Reached',
    [AnalyticsModelErrorTypeCodes.ModelTimeout]: 'Model Timeout',
    [AnalyticsModelErrorTypeCodes.SyntaxError]: 'Syntax Error',
    [AnalyticsModelErrorTypeCodes.MalformedResults]: 'Malformed model results',
    [AnalyticsModelErrorTypeCodes.NoExecuteMethod]: 'Model missing execute method',
    [AnalyticsModelErrorTypeCodes.RetainedInfoTooLarge]: 'Retained info too large',
};

const getStatusIconTitle = (rowData: IAnalyticsModelResponse) => {
    if (rowData.IsDeprecated) {
        return 'Deprecated';
    }
    return 'Disabled due to status';
};

export type AnalyticsModelsTableProps = Readonly<{
    location?: ILocationResponse;
    corporation?: ICorporationResponse;
}>;

export function AnalyticsModelsTable({
    location,
    corporation,
}: AnalyticsModelsTableProps) {
    const { showToast } = useNotificationContext();
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const queryClient = useQueryClient();
    const [selectedModel, setSelectedModel] = useState<IAnalyticsModelResponse | null>();

    const dataSource = useAnalyticsModelPagedDataSource({
        name: 'analytic-models',
        defaultFilter: {
            locationId: location?.Id,
            corporationId: corporation?.Id,
            nameSearch: undefined,
            includeGlobal: false,
        },
        defaultSort: [{ field: 'Name', dir: 'asc' }],
    });

    const { setFilter } = dataSource;

    const { mutate: deleteModel } = useMutation({
        mutationFn: async () => {
            if (selectedModel) {
                await sdClient.deleteAnalyticsModel({ modelId: selectedModel.Id });
                queryClient.invalidateQueries({
                    queryKey: ['analytic-models'],
                });
                setDeleteOpen(false);
            }
        },

        onSuccess: () =>
            showToast({ type: 'success', content: 'Successfully deleted model' }),

        onError: () => showToast({ type: 'error', content: 'Failed to delete model' }),
    });

    const columns = useMemo<TableColumn<IAnalyticsModelResponse>[]>(
        () => [
            {
                header: () => 'Model',
                id: 'Name',
                value: ({ rowData }) => (
                    <div className="flex-row align-center">
                        <ArrowLinkCell
                            title="Select model"
                            to={getTabRouteFromNavType(
                                corporation?.Id,
                                location?.Id,
                                'repository',
                                rowData.Id,
                            )}
                        >
                            <BooleanStatusIcon
                                status={
                                    !rowData.IsDeprecated &&
                                    rowData.ErrorType === undefined
                                }
                                trueState={{ title: 'Approved' }}
                                falseState={{ title: getStatusIconTitle(rowData) }}
                            />
                            <span className="pl-10">
                                {rowData.Name}
                                {rowData.IsGlobal ? ' (KCF)' : ''}
                            </span>
                        </ArrowLinkCell>
                    </div>
                ),
                primary: true,
                sortOn: 'Name',
            },
            {
                header: () => 'Version',
                id: 'Version',
                value: ({ rowData }) => rowData.Version,
                sortOn: 'Version',
            },
            {
                header: () => 'Created By',
                id: 'CreatedBy',
                value: ({ rowData }) => (
                    <span>
                        {rowData.CreatedBy.UserFirstName}
                        &nbsp;
                        {rowData.CreatedBy.UserLastName}
                    </span>
                ),
            },
            {
                header: () => 'Creation Date',
                id: 'Created',
                value: ({ rowData }) => <DateRelativeToNow date={rowData.Created} />,
                sortOn: 'Created',
            },
            {
                header: () => 'Status',
                id: 'ErrorType',
                value: ({ rowData }) =>
                    rowData.ErrorType ? ModelErrors[rowData.ErrorType] : 'Successful',
            },
        ],
        [corporation?.Id, location?.Id],
    );

    useEffect(() => {
        setFilter((current) => ({
            ...current,
            locationId: location?.Id,
            corporationId: corporation?.Id,
        }));
    }, [corporation?.Id, location?.Id, setFilter]);

    return (
        <TablePageLayout>
            <CRUDTable<IAnalyticsModelResponse, AnalyticsModelsFilter>
                columns={columns}
                dataSource={dataSource}
                onCreate={() => setCreateModalOpen(true)}
                onDelete={(record) => {
                    setSelectedModel(record);
                    setDeleteOpen(true);
                }}
                onEdit={(record) => {
                    setSelectedModel(record);
                    setEditModalOpen(true);
                }}
                rowKey={(data) => `model-${data.Id}`}
                noResultMessage="No analytics models found."
            />

            {createModalOpen && (
                <CreateAnalyticsModelModal
                    onClose={() => setCreateModalOpen(false)}
                    corporationId={corporation?.Id}
                    locationId={location?.Id}
                />
            )}
            {selectedModel && editModalOpen && (
                <EditAnalyticsModelModal
                    model={selectedModel}
                    onClose={() => setEditModalOpen(false)}
                />
            )}
            {selectedModel && deleteOpen && (
                <ConfirmModal
                    title={`Delete '${selectedModel.Name}'`}
                    actionText="delete this model? The data will no longer be available."
                    checkText="delete this model and its data"
                    actionButtonText="Delete"
                    onAction={deleteModel}
                    onClose={() => setDeleteOpen(false)}
                />
            )}
        </TablePageLayout>
    );
}
